/**
 * OAuth 콜백 페이지
 * 소셜 로그인 완료 후 리다이렉트되는 페이지
 * URL fragment에서 토큰 추출 후 저장, 프로젝트 목록으로 이동
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { setAuthToken } from "@/lib/apollo";

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetch } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // URL 파라미터에서 에러 확인
      const errorParam = searchParams.get("error");
      if (errorParam) {
        setError(decodeURIComponent(errorParam));
        return;
      }

      // URL fragment에서 OAuth 토큰 추출 (#oauth_token=xxx)
      const hash = window.location.hash;
      const tokenMatch = hash.match(/oauth_token=([^&]+)/);

      if (tokenMatch && tokenMatch[1]) {
        // 토큰 저장 (Capacitor 모바일 앱용)
        const token = decodeURIComponent(tokenMatch[1]);
        setAuthToken(token);

        // URL에서 hash 제거 (보안)
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }

      // 인증 상태 새로고침
      try {
        const result = await refetch();
        if (result.data?.me) {
          // 로그인 성공 - 프로젝트 목록으로 이동
          navigate("/projects", { replace: true });
        } else {
          // 인증 실패 (토큰이 없거나 유효하지 않음)
          setError("로그인에 실패했습니다. 다시 시도해주세요.");
        }
      } catch {
        setError("인증 확인 중 오류가 발생했습니다.");
      }
    };

    handleCallback();
  }, [navigate, refetch, searchParams]);

  // 에러 표시
  if (error) {
    return (
      <div className="safe-area-top safe-area-bottom flex h-full flex-col items-center justify-center bg-slate-900 px-6">
        <div className="w-full max-w-sm text-center">
          <div className="mb-4 text-4xl">⚠️</div>
          <h1 className="mb-2 text-xl font-bold text-white">로그인 실패</h1>
          <p className="mb-6 text-gray-400">{error}</p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="w-full rounded-lg bg-blue-600 py-3 font-medium text-white transition hover:bg-blue-700"
          >
            로그인 페이지로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 로딩 표시
  return (
    <div className="safe-area-top safe-area-bottom flex h-full flex-col items-center justify-center bg-slate-900 px-6">
      <div className="text-center">
        <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-blue-500 mx-auto" />
        <p className="text-gray-400">로그인 처리 중...</p>
      </div>
    </div>
  );
}
